import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [authFailureCount, setAuthFailureCount] = useState(0);
  const [adminDisplayEnabled, setAdminDisplayEnabled] = useState(() => {
    // Check localStorage for saved preference, default to null (will be set when user loads)
    const saved = localStorage.getItem('adminDisplayEnabled');
    return saved !== null ? saved === 'true' : null;
  });

  const API_URL = process.env.REACT_APP_API_URL || '';

  const clearAuth = () => {
    setToken(null);
    setUser(null);
    setAdminDisplayEnabled(false);
    localStorage.removeItem('token');
    localStorage.removeItem('adminDisplayEnabled');
    delete axios.defaults.headers.common['Authorization'];
    setAuthFailureCount(0);
  };

  const fetchUser = async (authToken, retryCount = 0) => {
    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      const response = await axios.get(`${API_URL}/api/auth/me`);
      const userData = response.data;
      setUser(userData);
      
      // Reset failure count on successful auth
      setAuthFailureCount(0);
      
      // Handle admin display preference
      if (userData?.is_admin) {
        // If admin user and no saved preference, default to enabled
        const saved = localStorage.getItem('adminDisplayEnabled');
        if (saved === null) {
          // First time admin login - default to enabled
          setAdminDisplayEnabled(true);
          localStorage.setItem('adminDisplayEnabled', 'true');
        } else {
          // Use saved preference
          setAdminDisplayEnabled(saved === 'true');
        }
      } else {
        // If user is not admin, always disable admin display
        setAdminDisplayEnabled(false);
        localStorage.setItem('adminDisplayEnabled', 'false');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user:', error);
      
      // Handle 401 errors with retry logic
      if (error.response && error.response.status === 401) {
        const newFailureCount = authFailureCount + 1;
        setAuthFailureCount(newFailureCount);
        
        // Retry once after a short delay (in case of temporary server restart)
        if (retryCount === 0 && newFailureCount <= 2) {
          console.log('401 error, retrying after delay...');
          setTimeout(() => {
            fetchUser(authToken, 1);
          }, 2000); // Wait 2 seconds before retry
          return;
        }
        
        // Only clear token after multiple consecutive failures (3+)
        // This handles temporary server restarts gracefully
        if (newFailureCount >= 3) {
          console.log('Multiple 401 errors, clearing authentication');
          clearAuth();
        } else {
          // Keep token but don't set user - allows retry on next request
          setUser(null);
        }
      } else {
        // For server errors (502, 500, etc.), keep the token but don't set user
        // This way when the server comes back, the token will still be valid
        setUser(null);
        // Reset failure count on non-auth errors
        setAuthFailureCount(0);
      }
      setLoading(false);
    }
  };

  // Set up axios interceptor for global 401 handling
  useEffect(() => {
    let failureCount = 0;
    
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        // Reset failure count on successful requests
        if (failureCount > 0) {
          failureCount = 0;
          setAuthFailureCount(0);
        }
        return response;
      },
      async (error) => {
        // Handle 401 errors globally
        if (error.response && error.response.status === 401) {
          const currentToken = localStorage.getItem('token');
          
          if (currentToken) {
            failureCount += 1;
            setAuthFailureCount(prev => {
              const newCount = prev + 1;
              // Only clear auth after multiple consecutive 401s (3+)
              if (newCount >= 3) {
                console.log('Multiple 401 errors detected, clearing authentication');
                // Use setTimeout to avoid state update during render
                setTimeout(() => {
                  clearAuth();
                }, 0);
              } else {
                // For first/second 401, keep token but let the request fail
                // This allows components to handle the error gracefully
                console.log(`401 error (${newCount}/3), keeping token for retry`);
              }
              return newCount;
            });
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  useEffect(() => {
    // Check for token in URL hash (from OAuth callback) - this takes priority
    const hash = window.location.hash;
    if (hash && hash.includes('token=')) {
      const urlToken = hash.split('token=')[1].split('&')[0];
      if (urlToken) {
        localStorage.setItem('token', urlToken);
        setToken(urlToken);
        // Clean up URL immediately
        window.history.replaceState({}, document.title, '/');
        // Fetch user with the new token
        fetchUser(urlToken);
        return;
      }
    }
    
    // Otherwise check localStorage for existing token
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      fetchUser(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (provider) => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/${provider}`);
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error(`Error initiating ${provider} login:`, error);
      throw error;
    }
  };

  const handleOAuthCallback = async (provider, code) => {
    // This is now handled by the backend redirect, but keeping for backwards compatibility
    try {
      const response = await axios.get(`${API_URL}/api/auth/${provider}/callback`, {
        params: { code },
      });
      // If we get here, it means the redirect didn't work, so handle manually
      if (response.data && response.data.access_token) {
        const { access_token, user: userData } = response.data;
        setToken(access_token);
        localStorage.setItem('token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setUser(userData);
        return true;
      }
      return false;
    } catch (error) {
      console.error(`Error completing ${provider} login:`, error);
      return false;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setAdminDisplayEnabled(false);
    localStorage.removeItem('token');
    localStorage.removeItem('adminDisplayEnabled');
    delete axios.defaults.headers.common['Authorization'];
  };

  const toggleAdminDisplay = () => {
    const newValue = !adminDisplayEnabled;
    setAdminDisplayEnabled(newValue);
    localStorage.setItem('adminDisplayEnabled', String(newValue));
  };

  const refreshUser = async () => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      await fetchUser(storedToken);
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    handleOAuthCallback,
    isAuthenticated: !!user,
    adminDisplayEnabled: adminDisplayEnabled && user?.is_admin,
    toggleAdminDisplay,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

